import React from "react"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import { FLOAT_COMPARISON_OPERATOR } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import {
  AUDIO_OBSERVATION_HIGHLIGHT_COLOR,
  AUDIO_REGION_COLOR_BY_CLASSIFICATION,
  interpolateAudioRegionColor,
} from "../audio-metric-constants"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"
import { WithLLMAudioRegionsMixin } from "../mixins/WithLLMAudioRegionsMixin"
import { NumericalLLMMetricRecord } from "./NumericalLLMMetricRecord"

type BaseTestCaseRecord = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

export class AudioNumericalLLMMetricRecord extends WithLLMAudioRegionsMixin(
  WithAudioFilterMixin(NumericalLLMMetricRecord),
) {
  documentation = this.metric_metadata?.question || this.description

  _renderDeepDive(
    mo: RunMetricOutput,
    _testCase?: BaseTestCaseRecord,
  ): React.ReactNode {
    const audioSection = this._renderAudioObservationsSection(mo)
    if (!audioSection) {
      return super._renderDeepDive(mo)
    }
    return <div className="space-y-8">{audioSection}</div>
  }

  get supportsAudioSegmentReview(): boolean {
    return false
  }

  renderMetricType(): React.ReactNode {
    return "Audio Numerical"
  }

  // Per-span score gradient: interpolates warning-orange→success (or reversed for
  // lower-is-better metrics) using float_value across the configured range.
  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      const observations = mo.subvalues_by_timestamp
      if (!observations || observations.length === 0) {
        return []
      }

      const minValue = this.metric_metadata?.min_value ?? 0
      const maxValue = this.metric_metadata?.max_value ?? 1
      const range = maxValue - minValue

      // Determine polarity from target_condition operator.
      // GT/GTE = higher is better (t goes 0→1 as score goes min→max).
      // LT/LTE = lower is better (t goes 1→0 as score goes min→max).
      // EQ/NEQ and absent condition produce no meaningful gradient; fall back to default.
      const tc = this.metric_metadata?.target_condition
      const operator =
        tc && "comparison_operator" in tc ? tc.comparison_operator : null
      const lowerIsBetter =
        operator === FLOAT_COMPARISON_OPERATOR.LT ||
        operator === FLOAT_COMPARISON_OPERATOR.LTE
      const hasDirection =
        operator === FLOAT_COMPARISON_OPERATOR.GT ||
        operator === FLOAT_COMPARISON_OPERATOR.GTE ||
        lowerIsBetter

      return observations.map((obs, idx) => {
        const start = obs.start_offset ?? obs.timestamp ?? 0
        const end = obs.end_offset ?? start + 0.5

        let color: string
        if (obs.float_value == null || !hasDirection || range === 0) {
          color = AUDIO_REGION_COLOR_BY_CLASSIFICATION.default
        } else {
          const normalized = (obs.float_value - minValue) / range
          const t = lowerIsBetter ? 1 - normalized : normalized
          color = interpolateAudioRegionColor(t)
        }

        const label =
          obs.float_value != null ? String(obs.float_value) : undefined

        return {
          id: `audio-obs:${idx}`,
          start,
          end,
          color: color ?? AUDIO_OBSERVATION_HIGHLIGHT_COLOR,
          content: label,
          drag: false,
          resize: false,
        }
      })
    } catch {
      return []
    }
  }
}
